import { Box, Icon, Text, Tag } from '@nimbus-ds/components';
import {
  BoxPackedIcon,
  BrowserIcon,
  ChevronLeftIcon,
  FileIcon,
  GlobeIcon,
  PencilIcon,
  TruckIcon,
  UserIcon,
  WalletIcon,
} from '@nimbus-ds/icons';
import { Menu } from '@nimbus-ds/patterns';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Country } from '@tiendanube/common/src/enums';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import ShowByRole from 'App/components/ShowByRole';
import { NEW_ADMIN_INVOICES } from 'App/featuresFlags';
import { useHandleMenuClick, useNavegate } from 'App/hooks';
import { ACCOUNT_ROUTES } from 'domains/Account/accountRoutes';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import {
  useGetCountry,
  useGetTags,
  useIsPlanLojinha,
} from 'domains/Auth/hooks';
import { CONFIGURATIONS_ROUTES } from 'domains/Configurations/configurationsRoutes';
import { ExternalCodesMenu } from 'domains/Configurations/ExternalCodes/ExternalCodesMenu';
import { NotificationsMenu } from 'domains/Configurations/Notifications/NotificationsMenu';
import { SeoRedirectsMenu } from 'domains/Configurations/SeoRedirects';
import { seoRedirectsRoutes } from 'domains/Configurations/SeoRedirects/seoRedirectsRoutes';
import { SettingsCheckoutMenu } from 'domains/Configurations/SettingsCheckout';
import { WhatsappMenu } from 'domains/Configurations/Whatsapp';
import { InfoMessageMenu } from 'domains/Online/InfoMessage';
import { ContactInfoMenu } from 'domains/Online/Themes/pages/ContactInfoPage/ContactInfoMenu';
import { PAYMENTS_BASE_ROUTE } from 'domains/Payments/paymentsRoutes';
import { SHIPPING_METHODS_BASE_ROUTE } from 'domains/Shipping/DeliveryMethods/shippingRoutes';
import {
  menuInvoicesClick,
  menuPaymentsClick,
  menuPermissionsClick,
  menuSecondaryDomainsClick,
} from './tracking';
import { useMenuScrollShadow, useSectionActive } from '../../hooks';

const path = {
  locations: '/settings/locations',
  payments: PAYMENTS_BASE_ROUTE,
  shippingMethods: SHIPPING_METHODS_BASE_ROUTE,
  email: '/notifications/',
  infoMessage: '/info_message/',
  i18n: CONFIGURATIONS_ROUTES.i18n,
  checkout: '/settings/checkout/',
  redirects301: seoRedirectsRoutes.redirects,
  domains: CONFIGURATIONS_ROUTES.domains,
  metafields: '/settings/metafields',
  invoices: '/settings/invoices',
};

interface SettingsMenuProps {
  visible: boolean;
}

function SettingsMenu({ visible }: Readonly<SettingsMenuProps>) {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const { goTo } = useNavegate();
  const isPlanLojinha = useIsPlanLojinha();
  const { validateRoles } = useRoles();
  const canGenerateInvoices = useGetTags().includes(NEW_ADMIN_INVOICES);
  const menuBodyRef = useMenuScrollShadow();
  const isSectionActive = useSectionActive();
  const country = useGetCountry();

  const { handleClickItem } = useHandleMenuClick();
  const { isExpandedMenu } = useDesktopMenuContext();

  const handleClickDomains = () => {
    menuSecondaryDomainsClick();
    handleClickItem();
  };

  const handleClickPayments = () => {
    menuPaymentsClick();
    handleClickItem();
  };

  const handleClickInvoices = () => {
    menuInvoicesClick();
    handleClickItem();
  };

  const handleClickPermissions = () => {
    menuPermissionsClick();
    handleClickItem();
  };

  const shippingAndPaymentTitle = () => {
    if (validateRoles(['shipping', 'locations']) && validateRoles('payments')) {
      return t('settingsMenu.payments.title');
    }
    return validateRoles(['shipping', 'locations'])
      ? t('settingsMenu.payments.title_only_shipping')
      : t('settingsMenu.payments.title_only_payments');
  };

  return (
    <div
      className={classNames('stratus--menu-container', 'stratus--submenu', {
        visible: visible,
      })}
    >
      <Menu expanded={isExpandedMenu}>
        <Menu.Header>
          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            width="100%"
            justifyContent={isExpandedMenu ? 'space-between' : 'center'}
          >
            <Box
              display="flex"
              gap="1"
              as="button"
              backgroundColor="transparent"
              borderColor="transparent"
              onClick={() => goTo('/')}
              alignItems="center"
              cursor="pointer"
              justifyContent="flex-start"
            >
              <Icon
                color="neutral-textHigh"
                source={<ChevronLeftIcon size={14} />}
              />

              {isExpandedMenu && (
                <Text
                  fontWeight="medium"
                  lineClamp={1}
                  color="neutral-textHigh"
                >
                  {t('settingsMenu.title')}
                </Text>
              )}
            </Box>
          </Box>
        </Menu.Header>
        <div className="stratus--menu-body" ref={menuBodyRef}>
          <Menu.Body>
            <ShowByRole
              includeRoles={['full', 'shipping', 'locations', 'payments']}
            >
              <Menu.Section title={shippingAndPaymentTitle()}>
                <ShowByRole includeRoles="payments">
                  <Menu.Button
                    as={Link}
                    to={path.payments}
                    startIcon={WalletIcon}
                    onClick={handleClickPayments}
                    active={pathname.startsWith(path.payments)}
                    label={t('settingsMenu.payments.methods')}
                  />
                </ShowByRole>
                <ShowByRole includeRoles={['shipping']}>
                  <Menu.Button
                    as={Link}
                    to={path.shippingMethods}
                    startIcon={TruckIcon}
                    onClick={handleClickItem}
                    active={pathname.startsWith(path.shippingMethods)}
                    label={t('settingsMenu.payments.shippingMethods')}
                  />
                </ShowByRole>
                <ShowByRole includeRoles={['locations']}>
                  <Menu.Button
                    as={Link}
                    to={path.locations}
                    startIcon={BoxPackedIcon}
                    onClick={handleClickItem}
                    active={pathname.startsWith(path.locations)}
                    label={t('settingsMenu.payments.locations')}
                  />
                </ShowByRole>
                {canGenerateInvoices && country === Country.BR && (
                  <ShowByRole includeRoles="payments">
                    <Menu.Button
                      as={Link}
                      to={path.invoices}
                      startIcon={FileIcon}
                      onClick={handleClickInvoices}
                      active={pathname.startsWith(path.invoices)}
                      label={t('settingsMenu.payments.invoices')}
                    >
                      <Tag appearance="primary">
                        <Text color="primary-textLow">Novo</Text>
                      </Tag>
                    </Menu.Button>
                  </ShowByRole>
                )}
              </Menu.Section>
            </ShowByRole>

            <ShowByRole includeRoles="emails">
              <Menu.Section title={t(`settingsMenu.communication.title`)}>
                <ShowByRole includeRoles={['full']}>
                  <>
                    <ContactInfoMenu />
                    <WhatsappMenu />
                  </>
                </ShowByRole>
                <NotificationsMenu />
              </Menu.Section>
            </ShowByRole>

            <ShowByRole includeRoles={['full', 'preferences_checkout']}>
              <Menu.Section title={t('settingsMenu.checkout.title')}>
                <ShowByRole includeRoles="preferences_checkout">
                  <SettingsCheckoutMenu />
                </ShowByRole>
                <ShowByRole includeRoles={['full']}>
                  <InfoMessageMenu />
                </ShowByRole>
              </Menu.Section>
            </ShowByRole>

            <ShowByRole
              includeRoles={[
                'full',
                'preferences_checkout',
                'preferences_advanced',
                'seo_redirects',
                'domains',
                'products',
                'orders',
                'customers',
              ]}
            >
              {!isPlanLojinha && (
                <Menu.Section title={t('settingsMenu.advanced.other')}>
                  <ShowByRole includeRoles="full">
                    <Menu.Button
                      as={Link}
                      to={ACCOUNT_ROUTES.users}
                      startIcon={UserIcon}
                      onClick={handleClickPermissions}
                      active={isSectionActive(ACCOUNT_ROUTES.users)}
                      label={t('settingsMenu.advanced.permissions')}
                    />
                  </ShowByRole>
                  <ShowByRole includeRoles="domains">
                    <Menu.Button
                      as={Link}
                      to={path.domains}
                      startIcon={BrowserIcon}
                      onClick={handleClickDomains}
                      active={pathname.startsWith(path.domains)}
                      label={t('settingsMenu.advanced.domains')}
                    />
                  </ShowByRole>
                  <ShowByRole includeRoles="preferences_advanced">
                    <ExternalCodesMenu />
                  </ShowByRole>
                  <ShowByRole includeRoles={['i18n']}>
                    <Menu.Button
                      as={Link}
                      to={path.i18n}
                      startIcon={GlobeIcon}
                      active={pathname.startsWith(path.i18n)}
                      label={t('settingsMenu.general.languages')}
                      onClick={handleClickItem}
                    />
                  </ShowByRole>
                  <ShowByRole includeRoles="seo_redirects">
                    <SeoRedirectsMenu />
                  </ShowByRole>
                  <ShowByRole
                    includeRoles={['full', 'products', 'customers', 'orders']}
                  >
                    <Menu.Button
                      as={Link}
                      to={path.metafields}
                      startIcon={PencilIcon}
                      onClick={handleClickItem}
                      active={pathname.startsWith(path.metafields)}
                      label={t('settingsMenu.advanced.metafields')}
                    />
                  </ShowByRole>
                </Menu.Section>
              )}
            </ShowByRole>
          </Menu.Body>
        </div>
      </Menu>
    </div>
  );
}

export default SettingsMenu;
